"use client";

import type { ReactNode } from "react";
import { useId } from "react";
import { cn } from "./cn";

export interface FieldProps {
  label: string;
  helper?: ReactNode;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: (ids: { fieldId: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * label -> control -> helper/error wrapper shared by Input/Textarea/Select.
 * Wires aria-describedby/aria-invalid so callers only pass the ids through.
 */
export function Field({ label, helper, error, required, htmlFor, children }: FieldProps) {
  const autoId = useId();
  const fieldId = htmlFor ?? autoId;
  const helperId = helper ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("form-field", error && "has-error")}>
      <label htmlFor={fieldId}>
        {label}
        {required ? " *" : null}
      </label>
      {children({ fieldId, describedBy })}
      {error ? (
        <span id={errorId} className="form-field-error" role="alert">
          {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="form-field-helper">
          {helper}
        </span>
      ) : null}
    </div>
  );
}
